import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { resolveAtcState, applyAtcState } from './atc-utils.js';

// State class on the ATC button when no size is chosen yet (pre-select off).
// The sticky button is soft-disabled: clickable so pressing it opens the size
// picker and prompts for a size.
const SELECT_SIZE_STATE_CLASS = 'add-to-cart--select-size';

// How long the "Please select a size" prompt stays visible in the sticky bar.
const SIZE_ERROR_DISPLAY_DURATION = 5000;

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    if (this.dataset.designMode === 'true') {
      // In the editor, show only when this block is selected
      const section = this.closest('.shopify-section');
      if (section) {
        section.addEventListener('shopify:block:select', (e) => {
          if (e.target === this || this.contains(e.target)) {
            this.#showStickyBar();
          }
        });
        section.addEventListener('shopify:block:deselect', (e) => {
          if (e.target === this || this.contains(e.target)) {
            this.#hideStickyBar();
          }
        });
      }
    } else {
      this.#setupIntersectionObserver();
    }

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    // Close sticky-dropdowns on outside click (Badgley-Mischka pattern).
    document.addEventListener('click', this.#handleOutsideClick, { signal });

    this.#getInitialQuantity();
  }

  /**
   * Closes any open sticky-dropdown when a click lands outside one.
   * @param {Event} event
   */
  #handleOutsideClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.closest && target.closest('.sticky-dropdown')) return;
    this.querySelectorAll('.sticky-dropdown--open').forEach((d) => {
      d.classList.remove('sticky-dropdown--open');
      const t = d.querySelector('.sticky-dropdown__trigger');
      if (t) t.setAttribute('aria-expanded', 'false');
    });
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          // Element is above viewport - show sticky bar
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Toggles the expandable content open/closed
   * Dead code path since the new bar layout has no expand toggle, kept for
   * back-compat with morph re-renders that might still emit it.
   */
  handleToggleClick = () => {
    const isExpanded = this.refs.stickyBar.dataset.expanded === 'true';
    this.#setExpanded(!isExpanded);
  };

  /**
   * Toggles `.show-variants` on the host for the mobile variant panel
   * (Badgley-Mischka pattern — see `_sticky-add-to-cart.scss`).
   * @param {Event} event
   */
  handleVariantsToggle = (event) => {
    event.preventDefault();
    this.classList.toggle('show-variants');
  };

  /**
   * Toggles a single sticky-dropdown open. Closes any siblings.
   * @param {Event} event
   */
  handleDropdownTriggerClick = (event) => {
    const trigger = /** @type {HTMLElement} */ (event.target).closest('.sticky-dropdown__trigger');
    if (!trigger) return;
    const dropdown = trigger.closest('.sticky-dropdown');
    if (!dropdown) return;
    const wasOpen = dropdown.classList.contains('sticky-dropdown--open');
    this.querySelectorAll('.sticky-dropdown--open').forEach((d) => {
      d.classList.remove('sticky-dropdown--open');
      const t = d.querySelector('.sticky-dropdown__trigger');
      if (t) t.setAttribute('aria-expanded', 'false');
    });
    if (!wasOpen) {
      dropdown.classList.add('sticky-dropdown--open');
      trigger.setAttribute('aria-expanded', 'true');
    }
  };

  /**
   * Handles size option button clicks in the sticky bar.
   * Syncs the selection to the main variant picker, which triggers
   * a variantUpdate event and morphs the sticky bar with new state.
   * @param {Event} event - The click event
   */
  handleSizeOptionClick = (event) => {
    // The click may land on a child span inside the label/input wrapper,
    // so walk up to the element that carries the data attributes.
    const target = /** @type {HTMLElement} */ (event.target);
    const btn = target.closest('[data-option-value]') || target;
    const optionName = btn.dataset?.optionName;
    const optionValue = btn.dataset?.optionValue;
    if (!optionName || !optionValue) return;

    // Update the sticky-dropdown trigger immediately (Badgley-Mischka
    // pattern). The server-rendered "Select a Size" label only changes if
    // the merchant has `settings.preselect_size` on, otherwise the morph
    // re-render would keep it stuck on "Select a Size" forever.
    const dropdown = btn.closest('.sticky-dropdown');
    if (dropdown) {
      const trigger = dropdown.querySelector('.sticky-dropdown__trigger');
      const selectedText = trigger?.querySelector('.sticky-dropdown__selected-text');
      if (selectedText) selectedText.textContent = optionValue.toUpperCase();
      // Move the --selected class to the clicked item.
      dropdown.querySelectorAll('.sticky-dropdown__item--selected').forEach((el) => {
        el.classList.remove('sticky-dropdown__item--selected');
      });
      btn.closest('.sticky-dropdown__item')?.classList.add('sticky-dropdown__item--selected');
      // Close the dropdown.
      dropdown.classList.remove('sticky-dropdown--open');
      trigger?.setAttribute('aria-expanded', 'false');
    }

    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantPicker) return;

    // Find the matching radio input in the main variant picker and check it
    const inputs = variantPicker.querySelectorAll(`input[type="radio"]`);
    for (const input of inputs) {
      const radioInput = /** @type {HTMLInputElement} */ (input);
      if (radioInput.value === optionValue && radioInput.name.startsWith(optionName)) {
        if (!radioInput.checked) {
          radioInput.checked = true;
          radioInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        break;
      }
    }
  };

  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;

    // No size chosen yet: don't attempt the add. Open the size picker right in
    // the sticky bar and surface the "Please select a size" prompt instead.
    if (
      this.refs.addToCartButton.classList.contains(SELECT_SIZE_STATE_CLASS) ||
      this.#targetAddToCartButton.classList.contains(SELECT_SIZE_STATE_CLASS)
    ) {
      this.#promptForSize();
      return;
    }

    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /** @type {number | undefined} */
  #sizeErrorTimeout;

  /**
   * Prompts the shopper to pick a size when they press Add to Cart without one.
   * Reveals the size options in the sticky bar (mobile panel + first dropdown)
   * and shows the "Please select a size" message.
   */
  #promptForSize() {
    // Reveal the mobile variant panel.
    this.classList.add('show-variants');

    // Open the first size dropdown so the choices are immediately visible.
    const firstDropdown = this.querySelector('.sticky-dropdown');
    if (firstDropdown) {
      this.querySelectorAll('.sticky-dropdown--open').forEach((d) => {
        d.classList.remove('sticky-dropdown--open');
        d.querySelector('.sticky-dropdown__trigger')?.setAttribute('aria-expanded', 'false');
      });
      firstDropdown.classList.add('sticky-dropdown--open');
      firstDropdown.querySelector('.sticky-dropdown__trigger')?.setAttribute('aria-expanded', 'true');
    }

    this.#showSizeError();
  }

  /** Shows the sticky bar's "Please select a size" prompt, auto-hiding it. */
  #showSizeError() {
    const sizeError = this.refs.sizeError;
    if (!sizeError) return;
    sizeError.hidden = false;
    if (this.#sizeErrorTimeout) clearTimeout(this.#sizeErrorTimeout);
    this.#sizeErrorTimeout = setTimeout(() => this.#hideSizeError(), SIZE_ERROR_DISPLAY_DURATION);
  }

  /** Hides the sticky bar's size prompt (e.g. once a size is picked). */
  #hideSizeError() {
    const sizeError = this.refs.sizeError;
    if (this.#sizeErrorTimeout) clearTimeout(this.#sizeErrorTimeout);
    if (sizeError) sizeError.hidden = true;
  }

  /**
   * Sets the expanded state and syncs the toggle button aria attribute
   * @param {boolean} expanded
   */
  #setExpanded(expanded) {
    this.refs.stickyBar.dataset.expanded = expanded ? 'true' : 'false';
    const toggle = this.querySelector('[ref="expandToggle"]');
    if (toggle) toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response. When it
    // isn't there we can't morph the bar, but we must still resolve the button:
    // #handleVariantSelected put it into its loading state, and returning here
    // without applying the state would leave that spinner running for good.
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    const newStickyBar = newStickyAddToCart?.querySelector('[ref="stickyBar"]');

    if (!newStickyBar) {
      this.#applyVariantAtcState(variant);
      return;
    }

    // Store current visibility + expanded state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const currentExpanded = this.refs.stickyBar.dataset.expanded === 'true';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Capture which size buttons are selected in the main variant picker
    // so we can sync the sticky after morph.
    const productId = this.dataset.productId;
    const mainPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    const mainChecked = new Map();
    if (mainPicker) {
      mainPicker.querySelectorAll('input[type="radio"]:checked').forEach((radio) => {
        const input = /** @type {HTMLInputElement} */ (radio);
        const optName = input.name?.split('-')[0]; // e.g. "SIZE" from "SIZE-xxx-id"
        if (optName) mainChecked.set(optName, input.value);
      });
    }

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility + expanded state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.#setExpanded(currentExpanded);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    // Sync sticky-dropdown triggers with the main picker's checked state.
    // Without this, the server-rendered HTML resets size triggers to
    // "Select a Size" whenever `settings.preselect_size` is off — even
    // after the user has actively picked a size. This mirrors the
    // post-morph ATC-text override below.
    if (mainChecked.size > 0) {
      this.querySelectorAll('.sticky-dropdown').forEach((dropdown) => {
        const optionName = /** @type {HTMLElement} */ (dropdown).dataset.optionName;
        if (!optionName) return;
        const selectedValue = mainChecked.get(optionName.toUpperCase()) || mainChecked.get(optionName);
        if (!selectedValue) return;

        const trigger = dropdown.querySelector('.sticky-dropdown__trigger');
        const selectedText = trigger?.querySelector('.sticky-dropdown__selected-text');
        if (selectedText) selectedText.textContent = selectedValue.toUpperCase();

        // Move the --selected class to the matching item.
        dropdown.querySelectorAll('.sticky-dropdown__item--selected').forEach((el) => {
          el.classList.remove('sticky-dropdown__item--selected');
        });
        const match = dropdown.querySelector(`[data-option-value="${CSS.escape(selectedValue)}"]`);
        match?.closest('.sticky-dropdown__item')?.classList.add('sticky-dropdown__item--selected');
      });
    }

    // Fix the ATC button text/state after morph.
    // Server returns "Select a Size" when preselect is off, but user already selected.
    // A null variant means no size is chosen → soft "Select a Size" state.
    this.#applyVariantAtcState(variant);

    // A real size is now selected — clear any lingering size prompt.
    if (variant) this.#hideSizeError();

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
  };

  /**
   * Resolves and applies the ATC state for a variant to the sticky button. This
   * is what takes the button out of the loading state set on `variantSelected`,
   * so every path through #handleVariantUpdate has to reach it — including the
   * one where the response has no sticky markup to morph.
   * @param {ProductVariant | null | undefined} variant
   */
  #applyVariantAtcState(variant) {
    const textEl = this.refs.addToCartButton?.querySelector('.sticky-add-to-cart__button-text');
    const comingSoon = this.dataset.comingSoon === 'true';

    // Server returns "Select a Size" when preselect is off even though the user
    // has already picked one, and a null variant means no size is chosen → soft
    // "Select a Size" state. Resolve from the variant rather than the markup.
    applyAtcState(this.refs.addToCartButton, textEl, resolveAtcState(variant, variant == null, comingSoon));
  }

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // Show loading state on the sticky button while the variant fetch runs
    this.refs.addToCartButton?.setAttribute('data-loading', 'true');

    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}

